import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  TextField,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Chip,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  InputAdornment,
  Switch,
  Snackbar,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import {
  Logout as LogoutIcon,
  ContentCopy as CopyIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Menu as MenuIcon,
} from '@mui/icons-material';
import { signOut, onAuthStateChanged, createUserWithEmailAndPassword, getAuth } from 'firebase/auth';
import { initializeApp, deleteApp } from 'firebase/app';
import { collection, getDocs, doc, getDoc, setDoc, query, orderBy, updateDoc, deleteDoc } from 'firebase/firestore';
import { auth, db, firebaseConfig } from '../../firebase';
import { usePlatformSettings } from '../../hooks/usePlatformSettings';
import { translations } from '../../utils/translations';

function AdminDashboard() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const { settings, updateSettings, loading: settingsLoading } = usePlatformSettings();
  
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('adminLanguage') || 'en';
  });
  const [currentTab, setCurrentTab] = useState(1); // Default to User Management tab
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const t = translations[language];

  useEffect(() => {
    localStorage.setItem('adminLanguage', language);
  }, [language]);

  const handleLanguageChange = (event, newLanguage) => {
    if (newLanguage) {
      setLanguage(newLanguage);
    }
  };
  
  // Platform settings state
  const [platformName, setPlatformName] = useState('');
  const [platformTagline, setPlatformTagline] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [settingsSaved, setSettingsSaved] = useState(false);
  
  // Leads state
  const [leads, setLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadDetailDialog, setLeadDetailDialog] = useState(false);
  
  // Users state
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  
  // Create user state
  const [createUserDialog, setCreateUserDialog] = useState(false);
  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserPhone, setNewUserPhone] = useState('');
  const [newUserRole, setNewUserRole] = useState('user');
  const [createUserError, setCreateUserError] = useState(null);
  const [createUserLoading, setCreateUserLoading] = useState(false);
  
  // Edit user state
  const [editUserDialog, setEditUserDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editUserName, setEditUserName] = useState('');
  const [editUserEmail, setEditUserEmail] = useState('');
  const [editUserPhone, setEditUserPhone] = useState('');
  const [editUserRole, setEditUserRole] = useState('user');
  const [editUserLoading, setEditUserLoading] = useState(false);
  const [editUserError, setEditUserError] = useState(null);
  
  // Delete user state
  const [deleteUserDialog, setDeleteUserDialog] = useState(false);
  const [deletingUser, setDeletingUser] = useState(null);
  const [deleteUserLoading, setDeleteUserLoading] = useState(false);
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  // Check authentication and admin role
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate('/admin');
        return;
      }

      setUser(user);

      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (!userDoc.exists() || userDoc.data().role !== 'admin') {
          navigate('/admin/leads');
          return;
        }
        setUserProfile(userDoc.data());
      } catch (err) {
        console.error('Error checking admin role:', err);
        navigate('/admin');
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  // Load settings when available
  useEffect(() => {
    if (!settingsLoading) {
      setPlatformName(settings.platformName || '');
      setPlatformTagline(settings.platformTagline || '');
      setCompanyName(settings.companyName || '');
    }
  }, [settings, settingsLoading]);

  // Load leads
  const loadLeads = async () => {
    setLeadsLoading(true);
    try {
      const leadsRef = collection(db, 'leads');
      const q = query(leadsRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const leadsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date(),
      }));
      setLeads(leadsData);
    } catch (err) {
      console.error('Error loading leads:', err);
    } finally {
      setLeadsLoading(false);
    }
  };

  // Load users
  const loadUsers = async () => {
    setUsersLoading(true);
    try {
      const usersRef = collection(db, 'users');
      const querySnapshot = await getDocs(usersRef);
      const usersData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date(),
      }));
      setUsers(usersData);
    } catch (err) {
      console.error('Error loading users:', err);
    } finally {
      setUsersLoading(false);
    }
  };

  // Load data when tab changes
  useEffect(() => {
    if (currentTab === 1) {
      loadUsers();
    }
  }, [currentTab]);

  const handleSaveSettings = async () => {
    const success = await updateSettings({
      platformName,
      platformTagline,
      companyName,
    });
    if (success) {
      setSettingsSaved(true);
      setTimeout(() => setSettingsSaved(false), 3000);
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
    navigate('/admin');
  };

  const generateRefCode = () => {
    return Math.random().toString(36).substring(2, 10).toUpperCase();
  };

  const formatPhoneNumber = (value) => {
    const phone = value.replace(/\D/g, '');
    if (phone.length < 4) return phone;
    if (phone.length < 7) return `(${phone.slice(0, 3)}) ${phone.slice(3)}`;
    return `(${phone.slice(0, 3)}) ${phone.slice(3, 6)}-${phone.slice(6, 10)}`;
  };

  const handleNewUserPhoneChange = (e) => {
    setNewUserPhone(formatPhoneNumber(e.target.value));
  };

  const handleEditUserPhoneChange = (e) => {
    setEditUserPhone(formatPhoneNumber(e.target.value));
  };

  // Filter users based on search term
  const filteredUsers = users.filter(u => {
    if (!userSearchTerm.trim()) return true;
    const search = userSearchTerm.toLowerCase().trim();
    const nameMatch = (u.name || '').toLowerCase().includes(search);
    const emailMatch = (u.email || '').toLowerCase().includes(search);
    const phoneDigits = (u.phone || '').replace(/\D/g, '');
    const searchDigits = search.replace(/\D/g, '');
    const phoneMatch = searchDigits.length > 0 && phoneDigits.includes(searchDigits);
    return nameMatch || emailMatch || phoneMatch;
  });

  // Open edit dialog
  const handleEditUser = (u) => {
    setEditingUser(u);
    setEditUserName(u.name || '');
    setEditUserEmail(u.email || '');
    setEditUserPhone(u.phone || '');
    setEditUserRole(u.role || 'user');
    setEditUserError(null);
    setEditUserDialog(true);
  };

  // Save edited user
  const handleSaveEditUser = async () => {
    if (!editingUser) return;
    
    setEditUserLoading(true);
    setEditUserError(null);
    
    try {
      await updateDoc(doc(db, 'users', editingUser.id), {
        name: editUserName.trim(),
        email: editUserEmail.trim().toLowerCase(),
        phone: editUserPhone || null,
        role: editUserRole,
        updatedAt: new Date(),
      });
      
      // Update local state
      setUsers(prev => prev.map(u => 
        u.id === editingUser.id 
          ? { ...u, name: editUserName.trim(), email: editUserEmail.trim().toLowerCase(), phone: editUserPhone || null, role: editUserRole }
          : u
      ));
      
      setEditUserDialog(false);
      setSnackbar({ open: true, message: t.admin.userUpdated });
    } catch (err) {
      console.error('Error updating user:', err);
      setEditUserError('Failed to update user. Please try again.');
    } finally {
      setEditUserLoading(false);
    }
  };

  // Toggle user disabled status
  const handleToggleUserStatus = async (u) => {
    try {
      const newStatus = !u.disabled;
      await updateDoc(doc(db, 'users', u.id), {
        disabled: newStatus,
        updatedAt: new Date(),
      });
      
      // Update local state
      setUsers(prev => prev.map(user => 
        user.id === u.id ? { ...user, disabled: newStatus } : user
      ));
      
      setSnackbar({ open: true, message: newStatus ? t.admin.userDisabled : t.admin.userEnabled });
    } catch (err) {
      console.error('Error toggling user status:', err);
    }
  };

  // Open delete confirmation
  const handleDeleteUserClick = (u) => {
    setDeletingUser(u);
    setDeleteUserDialog(true);
  };

  // Confirm delete user
  const handleConfirmDeleteUser = async () => {
    if (!deletingUser) return;
    
    setDeleteUserLoading(true);
    
    try {
      await deleteDoc(doc(db, 'users', deletingUser.id));
      
      // Update local state
      setUsers(prev => prev.filter(u => u.id !== deletingUser.id));
      
      setDeleteUserDialog(false);
      setDeletingUser(null);
      setSnackbar({ open: true, message: t.admin.userDeleted });
    } catch (err) {
      console.error('Error deleting user:', err);
    } finally {
      setDeleteUserLoading(false);
    }
  };

  // Format answer labels for display
  const formatAnswerLabel = (key, value) => {
    const labels = {
      accidentType: {
        label: 'Accident Type',
        values: {
          rearEnd: 'Rear-end collision',
          tBone: 'T-bone (side impact)',
          headOn: 'Head-on collision',
          motorcycle: 'Motorcycle accident',
          truck: 'Truck / Commercial vehicle',
          pedestrian: 'Pedestrian / Bicycle',
        }
      },
      erVisit: {
        label: 'ER/Hospital Visit',
        values: { yes: 'Yes', no: 'No' }
      },
      injurySeverity: {
        label: 'Injury Severity',
        values: {
          none: 'No injury / soreness only',
          softTissue: 'Soft tissue (whiplash, strains)',
          fracture: 'Fracture',
          surgery: 'Surgery required',
          permanent: 'Permanent injury / disability',
        }
      },
      missedWork: {
        label: 'Missed Work',
        values: {
          no: 'No',
          days: '1–7 days',
          weeks: '1–4 weeks',
          months: '1+ months',
        }
      },
      fault: {
        label: 'Other Driver at Fault',
        values: { yes: 'Yes', unsure: 'Unsure', no: 'No' }
      },
      policeReport: {
        label: 'Police Report',
        values: { yes: 'Yes', no: 'No' }
      },
      city: {
        label: 'City',
        values: null // free text
      }
    };
    
    const config = labels[key];
    if (!config) return { label: key, value: value };
    
    return {
      label: config.label,
      value: config.values ? (config.values[value] || value) : value
    };
  };

  const handleViewLead = async (lead) => {
    setSelectedLead(lead);
    setLeadDetailDialog(true);
    
    // Auto-update status from 'new' to 'read' when viewing
    if (lead.status === 'new' || !lead.status) {
      try {
        await updateDoc(doc(db, 'leads', lead.id), {
          status: 'read',
          readAt: new Date(),
        });
        // Update local state
        setLeads(prevLeads => 
          prevLeads.map(l => 
            l.id === lead.id ? { ...l, status: 'read', readAt: new Date() } : l
          )
        );
        setSelectedLead({ ...lead, status: 'read', readAt: new Date() });
      } catch (err) {
        console.error('Error updating lead status:', err);
      }
    }
  };

  const handleMarkAsReferral = async () => {
    if (!selectedLead) return;
    
    try {
      await updateDoc(doc(db, 'leads', selectedLead.id), {
        status: 'referral',
        referredAt: new Date(),
      });
      // Update local state
      setLeads(prevLeads => 
        prevLeads.map(l => 
          l.id === selectedLead.id ? { ...l, status: 'referral', referredAt: new Date() } : l
        )
      );
      setSelectedLead({ ...selectedLead, status: 'referral', referredAt: new Date() });
    } catch (err) {
      console.error('Error marking lead as referral:', err);
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      new: { label: 'New', bgcolor: '#4A9E7F', color: '#FFFFFF' },
      read: { label: 'Read', bgcolor: '#C6D8D3', color: '#3A3335' },
      referral: { label: 'Referral', bgcolor: '#D81E5B', color: '#FFFFFF' },
    };
    const config = statusConfig[status] || statusConfig.new;
    return (
      <Chip 
        label={config.label} 
        size="small" 
        sx={{ bgcolor: config.bgcolor, color: config.color, fontWeight: 600 }} 
      />
    );
  };

  const handleCreateUser = async () => {
    setCreateUserError(null);
    setCreateUserLoading(true);

    // Create a secondary Firebase app to create the user
    // This prevents the admin from being signed out
    let secondaryApp = null;
    
    try {
      // Initialize a secondary Firebase app
      secondaryApp = initializeApp(firebaseConfig, 'SecondaryApp');
      const secondaryAuth = getAuth(secondaryApp);
      
      // Create user in Firebase Auth using the secondary app
      const userCredential = await createUserWithEmailAndPassword(secondaryAuth, newUserEmail, newUserPassword);
      const newUser = userCredential.user;

      // Create user document in Firestore (using main db, admin is still signed in)
      await setDoc(doc(db, 'users', newUser.uid), {
        name: newUserName.trim(),
        email: newUserEmail.trim().toLowerCase(),
        phone: newUserPhone || null,
        role: newUserRole,
        refCode: generateRefCode(),
        createdAt: new Date(),
        createdBy: user.uid,
        requiresPasswordChange: true, // Force password change on first login
        disabled: false,
      });

      // Sign out from secondary app and clean up
      await signOut(secondaryAuth);
      await deleteApp(secondaryApp);
      
      // Reset form and close dialog
      setNewUserName('');
      setNewUserEmail('');
      setNewUserPassword('');
      setNewUserPhone('');
      setNewUserRole('user');
      setCreateUserDialog(false);
      setCreateUserLoading(false);
      
      // Reload users list
      await loadUsers();
      
      alert(`User "${newUserEmail}" created successfully! They can now log in.`);
      
    } catch (err) {
      console.error('Error creating user:', err);
      
      // Clean up secondary app if it exists
      if (secondaryApp) {
        try {
          await deleteApp(secondaryApp);
        } catch (e) {
          // Ignore cleanup errors
        }
      }
      
      switch (err.code) {
        case 'auth/email-already-in-use':
          setCreateUserError('Email already in use.');
          break;
        case 'auth/weak-password':
          setCreateUserError('Password must be at least 6 characters.');
          break;
        case 'auth/invalid-email':
          setCreateUserError('Invalid email address.');
          break;
        default:
          setCreateUserError(`Failed to create user: ${err.message}`);
      }
      setCreateUserLoading(false);
    }
  };
  
  // Link an existing Firebase Auth user to Firestore (for users already in Auth)
  const handleLinkExistingUser = async () => {
    if (!newUserEmail) {
      setCreateUserError('Please enter the user\'s email address.');
      return;
    }
    
    setCreateUserError(null);
    setCreateUserLoading(true);

    try {
      // Generate a temporary UID based on email (admin will need to update with real UID)
      // For now, we'll create a placeholder that can be matched later
      const tempId = 'pending_' + newUserEmail.replace(/[^a-zA-Z0-9]/g, '_');
      
      await setDoc(doc(db, 'users', tempId), {
        email: newUserEmail.trim().toLowerCase(),
        role: newUserRole,
        refCode: generateRefCode(),
        createdAt: new Date(),
        createdBy: user.uid,
        status: 'pending_auth_link',
      });

      // Reload users list
      await loadUsers();

      // Reset form and close dialog
      setNewUserEmail('');
      setNewUserPassword('');
      setNewUserRole('user');
      setCreateUserDialog(false);
      
      alert(`User record created for "${newUserEmail}".\n\nNote: They need to be added to Firebase Auth separately, then their Firestore record should be updated with their Auth UID.`);
    } catch (err) {
      console.error('Error linking user:', err);
      setCreateUserError('Failed to create user record. Please try again.');
    } finally {
      setCreateUserLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  if (loading) {
    return (
      <Box 
        sx={{ 
          minHeight: '100vh', 
          bgcolor: '#FDF0D5', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center' 
        }}
      >
        <CircularProgress sx={{ color: '#EB5E55' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#FDF0D5' }}>
      {/* Header */}
      <Box sx={{ bgcolor: '#FFFFFF', borderBottom: '1px solid #C6D8D3', py: { xs: 1.5, sm: 2 } }}>
        <Container maxWidth="lg">
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between', 
            alignItems: { xs: 'stretch', md: 'center' },
            gap: { xs: 1.5, md: 0 },
          }}>
            {/* Top row on mobile: Back button and title */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: { xs: 1, sm: 2 },
              flexWrap: 'wrap',
            }}>
              <IconButton
                size="small"
                onClick={() => navigate('/admin/leads')}
                sx={{ 
                  color: '#5C5658', 
                  '&:hover': { color: '#EB5E55', bgcolor: 'rgba(235, 94, 85, 0.08)' },
                  display: { xs: 'flex', sm: 'none' },
                }}
              >
                <ArrowBackIcon />
              </IconButton>
              <Button
                variant="text"
                size="small"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/admin/leads')}
                sx={{ 
                  color: '#5C5658', 
                  '&:hover': { color: '#EB5E55', bgcolor: 'rgba(235, 94, 85, 0.08)' },
                  display: { xs: 'none', sm: 'flex' },
                }}
              >
                {t.admin.backToLeads}
              </Button>
              <Typography 
                variant={isMobile ? 'h6' : 'h5'}
                sx={{ 
                  fontWeight: 700,
                  color: '#3A3335',
                  fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' },
                }}
              >
                {t.admin.adminSettings}
              </Typography>
            </Box>
            
            {/* Bottom row on mobile: Controls */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: { xs: 1, sm: 2 },
              flexWrap: 'wrap',
              justifyContent: { xs: 'space-between', md: 'flex-end' },
            }}>
              {/* Language Toggle */}
              <ToggleButtonGroup
                value={language}
                exclusive
                onChange={handleLanguageChange}
                size="small"
                sx={{
                  '& .MuiToggleButton-root': {
                    color: '#5C5658',
                    borderColor: '#C6D8D3',
                    py: 0.5,
                    px: { xs: 1, sm: 1.5 },
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    '&.Mui-selected': {
                      color: '#EB5E55',
                      bgcolor: 'rgba(235, 94, 85, 0.08)',
                      borderColor: '#EB5E55',
                    },
                  },
                }}
              >
                <ToggleButton value="en">EN</ToggleButton>
                <ToggleButton value="es">ES</ToggleButton>
              </ToggleButtonGroup>
              
              {/* Email - hide on very small screens */}
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#5C5658',
                  display: { xs: 'none', sm: 'block' },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  maxWidth: { sm: '150px', md: 'none' },
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {user?.email}
              </Typography>
              
              {/* Logout button */}
              <Button
                variant="outlined"
                size="small"
                startIcon={!isMobile && <LogoutIcon />}
                onClick={handleLogout}
                sx={{
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  py: { xs: 0.5, sm: 0.75 },
                  px: { xs: 1, sm: 2 },
                  minWidth: { xs: 'auto', sm: '100px' },
                }}
              >
                {isMobile ? <LogoutIcon fontSize="small" /> : t.admin.logout}
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Tabs */}
      <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 4 }, px: { xs: 1.5, sm: 3 } }}>
        <Tabs 
          value={currentTab} 
          onChange={(e, v) => setCurrentTab(v)}
          variant={isMobile ? 'fullWidth' : 'standard'}
          sx={{
            '& .MuiTab-root': {
              color: '#5C5658',
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              minWidth: { xs: 'auto', sm: 160 },
              px: { xs: 1, sm: 2 },
              '&.Mui-selected': {
                color: '#EB5E55',
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#EB5E55',
            },
          }}
        >
          <Tab label={isMobile ? 'Settings' : t.admin.platformSettings} />
          <Tab label={isMobile ? 'Users' : t.admin.userManagement} />
        </Tabs>

        {/* Platform Settings Tab */}
        {currentTab === 0 && (
          <Paper sx={{ p: 4, mt: 3, bgcolor: '#FFFFFF', border: '1px solid #C6D8D3', borderRadius: '12px' }}>
            <Typography variant="h6" sx={{ mb: 3, color: '#3A3335' }}>
              {t.admin.platformSettings}
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 500 }}>
              <TextField
                fullWidth
                label={t.admin.platformName}
                value={platformName}
                onChange={(e) => setPlatformName(e.target.value)}
                helperText={t.admin.platformNameHelper}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
              />
              <TextField
                fullWidth
                label={t.admin.platformTagline}
                value={platformTagline}
                onChange={(e) => setPlatformTagline(e.target.value)}
                helperText={t.admin.platformTaglineHelper}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
              />
              <TextField
                fullWidth
                label={t.admin.companyName}
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                helperText={t.admin.companyNameHelper}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
              />

              {settingsSaved && (
                <Alert severity="success" sx={{ borderRadius: '10px' }}>{t.admin.settingsSaved}</Alert>
              )}

              <Button
                variant="contained"
                onClick={handleSaveSettings}
                sx={{ alignSelf: 'flex-start' }}
              >
                {t.admin.saveSettings}
              </Button>
            </Box>
          </Paper>
        )}

        {/* User Management Tab */}
        {currentTab === 1 && (
          <Paper sx={{ p: { xs: 2, sm: 4 }, mt: { xs: 2, sm: 3 }, bgcolor: '#FFFFFF', border: '1px solid #C6D8D3', borderRadius: '12px' }}>
            {/* Header and controls */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between', 
              alignItems: { xs: 'stretch', md: 'center' }, 
              mb: 3, 
              gap: 2 
            }}>
              <Typography variant="h6" sx={{ color: '#3A3335', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                {t.admin.users} ({filteredUsers.length})
              </Typography>
              
              {/* Search and buttons row */}
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 1.5, sm: 2 }, 
                alignItems: { xs: 'stretch', sm: 'center' },
              }}>
                {/* Search Input */}
                <TextField
                  size="small"
                  placeholder={t.admin.searchUsers}
                  value={userSearchTerm}
                  onChange={(e) => setUserSearchTerm(e.target.value)}
                  sx={{ 
                    minWidth: { xs: '100%', sm: 200, md: 250 },
                    '& .MuiOutlinedInput-root': { 
                      borderRadius: '10px',
                      bgcolor: 'rgba(253, 240, 213, 0.5)',
                      '& fieldset': { borderColor: '#C6D8D3' },
                      '&:hover fieldset': { borderColor: '#EB5E55' },
                      '&.Mui-focused fieldset': { borderColor: '#EB5E55' },
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#5C5658' }} />
                      </InputAdornment>
                    ),
                  }}
                />
                
                {/* Action buttons */}
                <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'flex-end', sm: 'flex-start' } }}>
                  <IconButton onClick={loadUsers} disabled={usersLoading}>
                    <RefreshIcon sx={{ color: '#EB5E55' }} />
                  </IconButton>
                  <Button
                    variant="contained"
                    startIcon={!isMobile && <AddIcon />}
                    onClick={() => setCreateUserDialog(true)}
                    sx={{ 
                      fontSize: { xs: '0.8rem', sm: '0.875rem' },
                      px: { xs: 1.5, sm: 2 },
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {isMobile ? <><AddIcon sx={{ mr: 0.5 }} /> Add</> : t.admin.addUser}
                  </Button>
                </Box>
              </Box>
            </Box>

            {usersLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress sx={{ color: '#EB5E55' }} />
              </Box>
            ) : filteredUsers.length === 0 ? (
              <Typography sx={{ color: '#5C5658', textAlign: 'center', py: 4 }}>
                {users.length === 0 ? t.admin.noUsersYet : t.admin.noResults}
              </Typography>
            ) : isMobile || isTablet ? (
              /* Mobile/Tablet: Card-based layout */
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {filteredUsers.map((u) => (
                  <Card 
                    key={u.id} 
                    sx={{ 
                      bgcolor: u.disabled ? 'rgba(220, 38, 38, 0.03)' : 'rgba(253, 240, 213, 0.3)',
                      border: '1px solid #C6D8D3',
                      borderRadius: '10px',
                      opacity: u.disabled ? 0.8 : 1,
                    }}
                  >
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      {/* Top row: Name and status */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                        <Box>
                          <Typography variant="subtitle1" sx={{ color: '#3A3335', fontWeight: 600, fontSize: '1rem' }}>
                            {u.name || 'No name'}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#5C5658', fontSize: '0.85rem' }}>
                            {u.email}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                          <Chip 
                            label={u.role} 
                            size="small"
                            sx={{ 
                              bgcolor: u.role === 'admin' ? 'rgba(235, 94, 85, 0.1)' : '#C6D8D3',
                              color: u.role === 'admin' ? '#EB5E55' : '#5C5658',
                              fontSize: '0.7rem',
                            }}
                          />
                          <Chip 
                            label={u.disabled ? 'Disabled' : 'Active'} 
                            size="small"
                            sx={{ 
                              bgcolor: u.disabled ? 'rgba(220, 38, 38, 0.1)' : 'rgba(74, 158, 127, 0.15)',
                              color: u.disabled ? '#DC2626' : '#3A6B5A',
                              fontWeight: 600,
                              fontSize: '0.7rem',
                            }}
                          />
                        </Box>
                      </Box>
                      
                      {/* Phone */}
                      {u.phone && (
                        <Typography variant="body2" sx={{ color: '#5C5658', mb: 1 }}>
                          📱 {u.phone}
                        </Typography>
                      )}
                      
                      <Divider sx={{ my: 1.5 }} />
                      
                      {/* Referral code row */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2" sx={{ color: '#5C5658' }}>Ref:</Typography>
                          <Typography variant="body2" sx={{ color: '#D81E5B', fontWeight: 600 }}>{u.refCode}</Typography>
                          <IconButton 
                            size="small"
                            onClick={() => {
                              copyToClipboard(`${window.location.origin}/estimate?ref=${u.refCode}`);
                              setSnackbar({ open: true, message: 'Link copied!' });
                            }}
                          >
                            <CopyIcon sx={{ fontSize: 14, color: '#EB5E55' }} />
                          </IconButton>
                        </Box>
                        
                        {/* Actions */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                          <IconButton 
                            size="small" 
                            onClick={() => handleEditUser(u)}
                            sx={{ color: '#5C5658', '&:hover': { color: '#EB5E55' } }}
                          >
                            <EditIcon sx={{ fontSize: 20 }} />
                          </IconButton>
                          <Switch
                            size="small"
                            checked={!u.disabled}
                            onChange={() => handleToggleUserStatus(u)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': { color: '#4A9E7F' },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#4A9E7F' },
                            }}
                          />
                          <IconButton 
                            size="small" 
                            onClick={() => handleDeleteUserClick(u)}
                            sx={{ color: '#5C5658', '&:hover': { color: '#DC2626' } }}
                          >
                            <DeleteIcon sx={{ fontSize: 20 }} />
                          </IconButton>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            ) : (
              /* Desktop: Table layout */
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: '#5C5658', fontWeight: 600 }}>{t.admin.status}</TableCell>
                      <TableCell sx={{ color: '#5C5658', fontWeight: 600 }}>{t.admin.userName}</TableCell>
                      <TableCell sx={{ color: '#5C5658', fontWeight: 600 }}>{t.admin.email}</TableCell>
                      <TableCell sx={{ color: '#5C5658', fontWeight: 600 }}>{t.admin.phone}</TableCell>
                      <TableCell sx={{ color: '#5C5658', fontWeight: 600 }}>{t.admin.role}</TableCell>
                      <TableCell sx={{ color: '#5C5658', fontWeight: 600 }}>{t.admin.referralCode}</TableCell>
                      <TableCell sx={{ color: '#5C5658', fontWeight: 600 }}>{t.admin.actions || 'Actions'}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredUsers.map((u) => (
                      <TableRow 
                        key={u.id} 
                        sx={{ 
                          '&:hover': { bgcolor: 'rgba(253, 240, 213, 0.5)' },
                          opacity: u.disabled ? 0.6 : 1,
                        }}
                      >
                        <TableCell>
                          <Chip 
                            label={u.disabled ? t.admin.userDisabled : t.admin.userEnabled} 
                            size="small"
                            sx={{ 
                              bgcolor: u.disabled ? 'rgba(220, 38, 38, 0.1)' : 'rgba(74, 158, 127, 0.15)',
                              color: u.disabled ? '#DC2626' : '#3A6B5A',
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ color: '#3A3335', fontWeight: 500 }}>{u.name || '—'}</TableCell>
                        <TableCell sx={{ color: '#3A3335' }}>{u.email}</TableCell>
                        <TableCell sx={{ color: '#5C5658' }}>{u.phone || '—'}</TableCell>
                        <TableCell>
                          <Chip 
                            label={u.role} 
                            size="small"
                            sx={{ 
                              bgcolor: u.role === 'admin' ? 'rgba(235, 94, 85, 0.1)' : '#C6D8D3',
                              color: u.role === 'admin' ? '#EB5E55' : '#5C5658',
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2" sx={{ color: '#D81E5B', fontWeight: 600 }}>{u.refCode}</Typography>
                            <IconButton 
                              size="small"
                              onClick={() => copyToClipboard(`${window.location.origin}/estimate?ref=${u.refCode}`)}
                            >
                              <CopyIcon sx={{ fontSize: 14, color: '#EB5E55' }} />
                            </IconButton>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <IconButton 
                              size="small" 
                              onClick={() => handleEditUser(u)}
                              sx={{ color: '#5C5658', '&:hover': { color: '#EB5E55' } }}
                            >
                              <EditIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                            <Switch
                              size="small"
                              checked={!u.disabled}
                              onChange={() => handleToggleUserStatus(u)}
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': { color: '#4A9E7F' },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#4A9E7F' },
                              }}
                            />
                            <IconButton 
                              size="small" 
                              onClick={() => handleDeleteUserClick(u)}
                              sx={{ color: '#5C5658', '&:hover': { color: '#DC2626' } }}
                            >
                              <DeleteIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        )}
      </Container>

      {/* Create User Dialog */}
      <Dialog 
        open={createUserDialog} 
        onClose={() => setCreateUserDialog(false)}
        fullScreen={isMobile}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            bgcolor: '#FFFFFF',
            border: isMobile ? 'none' : '1px solid #C6D8D3',
            borderRadius: isMobile ? 0 : '12px',
            m: isMobile ? 0 : 2,
          },
        }}
      >
        <DialogTitle sx={{ color: '#3A3335', fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>{t.admin.addNewUser}</DialogTitle>
        <DialogContent sx={{ minWidth: { xs: 'auto', sm: 400 } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
            <TextField
              fullWidth
              label={t.admin.userName}
              value={newUserName}
              onChange={(e) => setNewUserName(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />
            <TextField
              fullWidth
              label={t.admin.email}
              type="email"
              value={newUserEmail}
              onChange={(e) => setNewUserEmail(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />
            <TextField
              fullWidth
              label={t.admin.password}
              type="password"
              value={newUserPassword}
              onChange={(e) => setNewUserPassword(e.target.value)}
              helperText={t.admin.passwordHelper}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />
            <TextField
              fullWidth
              label={t.admin.userPhone}
              type="tel"
              value={newUserPhone}
              onChange={handleNewUserPhoneChange}
              placeholder="(555) 555-5555"
              helperText={t.admin.userPhoneHelper}
              inputProps={{ maxLength: 14 }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />
            <FormControl fullWidth>
              <InputLabel>{t.admin.role}</InputLabel>
              <Select
                value={newUserRole}
                label={t.admin.role}
                onChange={(e) => setNewUserRole(e.target.value)}
                sx={{ borderRadius: '10px' }}
              >
                <MenuItem value="user">{t.admin.userLeadGen}</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>
            
            <Alert severity="info" sx={{ borderRadius: '10px' }}>
              {t.admin.createUserNote}
            </Alert>

            {createUserError && (
              <Alert severity="error" sx={{ borderRadius: '10px' }}>{createUserError}</Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setCreateUserDialog(false)}>{t.admin.cancel}</Button>
          <Button 
            variant="contained" 
            onClick={handleCreateUser}
            disabled={!newUserEmail || !newUserPassword || createUserLoading}
          >
            {createUserLoading ? <CircularProgress size={24} sx={{ color: '#FFFFFF' }} /> : t.admin.createUser}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog 
        open={editUserDialog} 
        onClose={() => setEditUserDialog(false)}
        fullScreen={isMobile}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            bgcolor: '#FFFFFF',
            border: isMobile ? 'none' : '1px solid #C6D8D3',
            borderRadius: isMobile ? 0 : '12px',
            m: isMobile ? 0 : 2,
          },
        }}
      >
        <DialogTitle sx={{ color: '#3A3335', fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>{t.admin.editUser}</DialogTitle>
        <DialogContent sx={{ minWidth: { xs: 'auto', sm: 400 } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
            <TextField
              fullWidth
              label={t.admin.userName}
              value={editUserName}
              onChange={(e) => setEditUserName(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />
            <TextField
              fullWidth
              label={t.admin.email}
              type="email"
              value={editUserEmail}
              onChange={(e) => setEditUserEmail(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />
            <TextField
              fullWidth
              label={t.admin.userPhone}
              type="tel"
              value={editUserPhone}
              onChange={handleEditUserPhoneChange}
              placeholder="(555) 555-5555"
              inputProps={{ maxLength: 14 }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />
            <FormControl fullWidth>
              <InputLabel>{t.admin.role}</InputLabel>
              <Select
                value={editUserRole}
                label={t.admin.role}
                onChange={(e) => setEditUserRole(e.target.value)}
                sx={{ borderRadius: '10px' }}
              >
                <MenuItem value="user">{t.admin.userLeadGen}</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>

            {editUserError && (
              <Alert severity="error" sx={{ borderRadius: '10px' }}>{editUserError}</Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEditUserDialog(false)}>{t.admin.cancel}</Button>
          <Button 
            variant="contained" 
            onClick={handleSaveEditUser}
            disabled={!editUserEmail || editUserLoading}
          >
            {editUserLoading ? <CircularProgress size={24} sx={{ color: '#FFFFFF' }} /> : t.admin.saveChanges}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <Dialog
        open={deleteUserDialog}
        onClose={() => setDeleteUserDialog(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            bgcolor: '#FFFFFF',
            border: '1px solid #C6D8D3',
            borderRadius: '12px',
            m: { xs: 2, sm: 3 },
          },
        }}
      >
        <DialogTitle sx={{ color: '#DC2626' }}>{t.admin.deleteUser}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ borderRadius: '10px' }}>
            {t.admin.deleteUserConfirm}
          </Alert>
          {deletingUser && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(253, 240, 213, 0.5)', borderRadius: '10px' }}>
              <Typography variant="body2" sx={{ color: '#3A3335' }}>
                <strong>{deletingUser.name || 'No name'}</strong>
              </Typography>
              <Typography variant="body2" sx={{ color: '#5C5658' }}>
                {deletingUser.email}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteUserDialog(false)}>{t.admin.cancel}</Button>
          <Button 
            variant="contained" 
            onClick={handleConfirmDeleteUser}
            disabled={deleteUserLoading}
            sx={{ 
              bgcolor: '#DC2626', 
              '&:hover': { bgcolor: '#B91C1C' },
            }}
          >
            {deleteUserLoading ? <CircularProgress size={24} sx={{ color: '#FFFFFF' }} /> : t.admin.deleteUser}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Lead Detail Dialog */}
      <Dialog 
        open={leadDetailDialog} 
        onClose={() => setLeadDetailDialog(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            bgcolor: '#FFFFFF',
            border: isMobile ? 'none' : '1px solid #C6D8D3',
            borderRadius: isMobile ? 0 : '12px',
            m: isMobile ? 0 : 2,
          },
        }}
      >
        <DialogTitle sx={{ color: '#3A3335', borderBottom: '1px solid #C6D8D3' }}>
          Lead Details
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedLead && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Contact Information */}
              <Box>
                <Typography variant="subtitle2" sx={{ color: '#5C5658', mb: 1.5, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                  Contact Information
                </Typography>
                <Paper sx={{ p: 2, bgcolor: 'rgba(253, 240, 213, 0.5)', border: '1px solid #C6D8D3' }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#5C5658' }}>Name</Typography>
                      <Typography variant="body1" sx={{ color: '#3A3335', fontWeight: 500 }}>{selectedLead.name}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#5C5658' }}>Phone</Typography>
                      <Typography variant="body1" sx={{ color: '#3A3335', fontWeight: 500 }}>{selectedLead.phone}</Typography>
                    </Box>
                    <Box sx={{ gridColumn: '1 / -1' }}>
                      <Typography variant="caption" sx={{ color: '#5C5658' }}>Email</Typography>
                      <Typography variant="body1" sx={{ color: '#3A3335', fontWeight: 500 }}>{selectedLead.email}</Typography>
                    </Box>
                  </Box>
                </Paper>
              </Box>

              {/* Settlement Range */}
              <Box>
                <Typography variant="subtitle2" sx={{ color: '#5C5658', mb: 1.5, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                  Estimated Settlement Range
                </Typography>
                <Paper sx={{ p: 2, bgcolor: 'rgba(235, 94, 85, 0.08)', border: '1px solid rgba(235, 94, 85, 0.3)' }}>
                  <Typography variant="h5" sx={{ color: '#D81E5B', fontWeight: 700, textAlign: 'center' }}>
                    {selectedLead.range?.formattedMin} – {selectedLead.range?.formattedMax}
                  </Typography>
                </Paper>
              </Box>

              {/* Form Answers */}
              <Box>
                <Typography variant="subtitle2" sx={{ color: '#5C5658', mb: 1.5, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                  Form Responses
                </Typography>
                <Paper sx={{ p: 2, bgcolor: 'rgba(253, 240, 213, 0.5)', border: '1px solid #C6D8D3' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {selectedLead.answers && Object.entries(selectedLead.answers).map(([key, value]) => {
                      const formatted = formatAnswerLabel(key, value);
                      return (
                        <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5, borderBottom: '1px solid #C6D8D3', '&:last-child': { borderBottom: 'none' } }}>
                          <Typography variant="body2" sx={{ color: '#5C5658' }}>{formatted.label}</Typography>
                          <Typography variant="body2" sx={{ color: '#3A3335', fontWeight: 500, textAlign: 'right' }}>{formatted.value || '—'}</Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Paper>
              </Box>

              {/* Metadata */}
              <Box>
                <Typography variant="subtitle2" sx={{ color: '#5C5658', mb: 1.5, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                  Submission Details
                </Typography>
                <Paper sx={{ p: 2, bgcolor: 'rgba(253, 240, 213, 0.5)', border: '1px solid #C6D8D3' }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#5C5658' }}>Submitted</Typography>
                      <Typography variant="body2" sx={{ color: '#3A3335' }}>
                        {selectedLead.createdAt?.toLocaleString?.() || '—'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#5C5658' }}>Language</Typography>
                      <Typography variant="body2" sx={{ color: '#3A3335' }}>
                        {selectedLead.language === 'es' ? 'Spanish' : 'English'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#5C5658' }}>Referral Code</Typography>
                      <Typography variant="body2" sx={{ color: '#3A3335' }}>
                        {selectedLead.refCode || 'Direct (no referral)'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#5C5658' }}>Consent Given</Typography>
                      <Typography variant="body2" sx={{ color: selectedLead.consentGiven ? '#4A9E7F' : '#D81E5B' }}>
                        {selectedLead.consentGiven ? '✓ Yes' : '✗ No'}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #C6D8D3', justifyContent: 'space-between' }}>
          <Box>
            {selectedLead?.status !== 'referral' && (
              <Button 
                onClick={handleMarkAsReferral} 
                variant="contained"
                sx={{ 
                  bgcolor: '#D81E5B', 
                  '&:hover': { bgcolor: '#B8184D' },
                }}
              >
                Mark as Referral
              </Button>
            )}
            {selectedLead?.status === 'referral' && (
              <Chip 
                label="✓ Marked as Referral" 
                sx={{ bgcolor: '#D81E5B', color: '#FFFFFF', fontWeight: 600 }} 
              />
            )}
          </Box>
          <Button onClick={() => setLeadDetailDialog(false)} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ open: false, message: '' })}
        message={snackbar.message}
      />
    </Box>
  );
}

export default AdminDashboard;

